import { useEffect, useMemo } from 'react'
import type { MentionableEmployee } from '../../types/occurrences'
import { Avatar } from '../common/Avatar'

/** Altura máxima desejada da lista — também usada para decidir o lado em que
 * o popup abre (`MentionableTextarea.resolvePlacement`). */
export const MENTION_POPUP_MAX_HEIGHT = 256

/** O que o popup lê de um candidato — `MentionableEmployee` (Ocorrências) e
 * os colegas da empresa de Atendimento HUB satisfazem os dois. */
export type MentionCandidate = Pick<
  MentionableEmployee,
  | 'id'
  | 'name'
  | 'role'
  | 'google_picture'
  | 'microsoft_picture'
>

interface MentionPopupProps {
  candidates: MentionCandidate[]
  query: string
  activeIndex: number
  onHover: (index: number) => void
  onSelect: (employee: MentionCandidate) => void
  /**
   * Lado em que o popup abre. `top` (padrão de chat) vira `bottom` quando não
   * há espaço acima — sem isso a lista era cortada pelo topo da janela em
   * campos que ficam na parte de cima da tela (ex.: descrição da ocorrência).
   */
  placement?: 'top' | 'bottom'
  /** Altura máxima já limitada ao espaço disponível na direção escolhida. */
  maxHeight?: number
}

export function MentionPopup({
  candidates,
  query,
  activeIndex,
  onHover,
  onSelect,
  placement = 'top',
  maxHeight = MENTION_POPUP_MAX_HEIGHT,
}: MentionPopupProps) {
  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    return candidates
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 6)
  }, [candidates, query])

  useEffect(() => {
    if (activeIndex >= filtered.length && filtered.length > 0) {
      onHover(0)
    }
  }, [filtered.length, activeIndex, onHover])

  if (filtered.length === 0) return null

  return (
    <div
      className={`absolute left-0 z-30 w-full max-w-md overflow-hidden rounded-[5px] border border-border-subtle bg-surface shadow-lg ${
        placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
      }`}
    >
      <ul
        role="listbox"
        className="overflow-y-auto py-1"
        style={{ maxHeight }}
      >
        {filtered.map((emp, idx) => {
          const active = idx === activeIndex
          return (
            <li
              key={emp.id}
              role="option"
              aria-selected={active}
              onMouseEnter={() => onHover(idx)}
              onMouseDown={(e) => {
                e.preventDefault()
                onSelect(emp)
              }}
              className={`flex cursor-pointer items-center gap-2 px-3 py-2 ${
                active ? 'bg-[#eff6ff] dark:bg-blue-900/20' : 'bg-surface'
              }`}
            >
              <Avatar
                name={emp.name}
                picture={emp.google_picture ?? emp.microsoft_picture ?? null}
                size={24}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-body">{emp.name}</div>
                <div className="truncate text-[11px] text-muted">{emp.role}</div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function filterCandidates<T extends MentionCandidate>(
  candidates: T[],
  query: string,
): T[] {
  const q = query.toLowerCase()
  return candidates
    .filter((c) => !q || c.name.toLowerCase().includes(q))
    .slice(0, 6)
}
